import clsx from "clsx";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "../../i18n";
import type { V2PrivacyControls } from "../../privacy";
import { AngleDown, Gear } from "../icons/Icons";
import "../../scss/private_session_settings.scss";

type Policy = NonNullable<V2PrivacyControls["sessionPolicy"]>;

/**
 * Local device preferences; this component never requests a signature.
 * - "corner" (sign-in dialog): a gear in the window's top-right corner opens
 *   a small card; a click outside, Escape or the gear closes it.
 * - "menu" (account menu): a gear row like the other menu rows, opening the
 *   same settings in place.
 */
export const PrivateSessionSettings = ({
  controls,
  disabled = false,
  placement = "menu",
}: {
  controls: V2PrivacyControls;
  disabled?: boolean;
  placement?: "corner" | "menu";
}) => {
  const { t } = useTranslation("header");
  const id = useId();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const corner = placement === "corner";
  useEffect(() => {
    if (!open || !corner) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open, corner]);
  const policy = controls.sessionPolicy;
  if (!policy || !controls.updateSessionPolicy) return null;
  const update = (patch: Partial<Policy>) =>
    controls.updateSessionPolicy?.({ ...policy, ...patch });
  // Each label explains itself in the global tooltip (dotted underline, no
  // (i) icon); its control gets the same text as its description. The texts
  // sit outside the labels, so a label's name stays just the label.
  const tips = {
    timeout: t("lockAfterInactivityTip"),
    start: t("startInPrivateModeTip"),
    undo: t("undoBeforeSendingTip"),
  };
  const tip = (text: string) => ({
    "data-tooltip-id": "global-tip",
    "data-tooltip-content": text,
    "data-tooltip-class-name": "tooltip--explainer",
  });
  // A whole row toggles its switch; the switch reuses Rujira's toggle styles.
  const switchRow = (
    label: string,
    key: "start" | "undo",
    checked: boolean,
    change: (checked: boolean) => void
  ) => (
    <label className="private-session-settings__row">
      <span className="has-tip" {...tip(tips[key])}>
        {label}
      </span>
      <span className="toggle toggle--sm toggle--primary2">
        <input
          type="checkbox"
          disabled={disabled}
          checked={checked}
          aria-describedby={`${id}-${key}-tip`}
          onChange={(event) => change(event.target.checked)}
        />
        <span className="toggle__slider" />
      </span>
    </label>
  );
  const body = (
    <div id={`${id}-body`} className="private-session-settings__body">
      {corner && (
        <h3 className="private-session-settings__title">
          {t("privateSessionSettings")}
        </h3>
      )}
      {disabled && (
        <p className="private-session-settings__hint">
          {t("privateSessionSettingsBusy")}
        </p>
      )}
      <div className="private-session-settings__row">
        <label
          htmlFor={`${id}-timeout`}
          className="has-tip"
          {...tip(tips.timeout)}>
          {t("lockAfterInactivity")}
        </label>
        <select
          id={`${id}-timeout`}
          className="private-session-settings__select"
          aria-describedby={`${id}-timeout-tip`}
          disabled={disabled}
          value={policy.idleTimeoutMs}
          onChange={(event) => {
            const duration = Number(event.target.value);
            if (
              duration !== 300_000 &&
              duration !== 900_000 &&
              duration !== 3_600_000
            )
              return;
            update({ idleTimeoutMs: duration });
          }}>
          <option value={300_000}>{t("idleFiveMinutes")}</option>
          <option value={900_000}>{t("idleFifteenMinutes")}</option>
          <option value={3_600_000}>{t("idleOneHour")}</option>
        </select>
      </div>
      {switchRow(
        t("startInPrivateMode"),
        "start",
        policy.alwaysPrivate,
        (checked) => update({ alwaysPrivate: checked })
      )}
      {switchRow(
        t("undoBeforeSending"),
        "undo",
        policy.undoBeforeSending === true,
        (checked) => update({ undoBeforeSending: checked })
      )}
      {(["timeout", "start", "undo"] as const).map((key) => (
        <span key={key} id={`${id}-${key}-tip`} hidden>
          {tips[key]}
        </span>
      ))}
      {controls.sessionPolicySaved === false && (
        <p role="status">{t("privateSessionSettingsUnsaved")}</p>
      )}
    </div>
  );
  return (
    <div
      ref={root}
      className={clsx(
        "private-session-settings",
        `private-session-settings--${placement}`,
        open && "private-session-settings--open"
      )}
      onKeyDown={(event) => {
        // Escape closes the card first, not the dialog around it.
        if (!open || event.key !== "Escape") return;
        event.stopPropagation();
        setOpen(false);
        toggle.current?.focus();
      }}>
      <button
        ref={toggle}
        type="button"
        className={
          corner
            ? "private-session-settings__gear"
            : "private-session-settings__toggle"
        }
        aria-expanded={open}
        aria-controls={`${id}-body`}
        {...(corner && {
          "aria-label": t("privateSessionSettings"),
          "data-tooltip-id": "global-tip",
          "data-tooltip-content": t("privateSessionSettings"),
          "data-tooltip-hidden": open || undefined,
        })}
        onClick={() => setOpen((value) => !value)}>
        <Gear aria-hidden="true" />
        {!corner && (
          <>
            <span>{t("privateSessionSettings")}</span>
            <AngleDown
              aria-hidden="true"
              className="private-session-settings__chevron"
            />
          </>
        )}
      </button>
      {open &&
        (corner ? (
          <div className="private-session-settings__card">{body}</div>
        ) : (
          body
        ))}
    </div>
  );
};
